import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { LoginPage } from '@/components/LoginPage';
import { PaymentModal } from '@/components/PaymentModal';
import { CompanyListPage } from '@/components/CompanyListPage';
import { ReceiptPage } from '@/components/ReceiptPage';
import { EmailVerificationPage } from '@/components/EmailVerificationPage';
import { BirthdayVerificationPage } from '@/components/BirthdayVerificationPage';
import { ProfilePage } from '@/components/ProfilePage';
import { OrderHistoryPage } from '@/components/OrderHistoryPage';
import { AnnouncementsPage } from '@/components/AnnouncementsPage';
import { PaymentMethodPage } from '@/components/PaymentMethodPage';
import { TermsOfServicePage } from '@/components/TermsOfServicePage';
import { PrivacyPolicyPage } from '@/components/PrivacyPolicyPage';
import { PartnerVisibilitySettingsPage } from '@/components/PartnerVisibilitySettingsPage';
import { usePageModals } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import type { CartItem } from '@/components/CartModal';
import type { PaymentFlow, OrderHistoryItem, Company, OrderData } from '@/types';
import type { PaymentResult } from '@/components/PaymentModal';

/**
 * FullPageRouter Props
 * 외부에서 전달받아야 하는 최소한의 props만 정의
 * language, currency는 Context에서 가져옴
 */
interface FullPageRouterProps {
  // Payment (결제는 외부에서 관리 - 장바구니와 연동)
  showPayment: boolean;
  onPaymentClose: () => void;
  onPaymentSuccess: (data: PaymentResult) => void;
  allCartItems: CartItem[];
  allCartTotal: number;
  showTableOrderMenu: boolean;

  // Company List (결제 플로우의 일부)
  showCompanyList: boolean;
  onCompanyListBack: () => void;
  onCompanySelect: (company: Company) => void;

  // Receipt (결제 플로우의 일부)
  paymentFlow: PaymentFlow;
  orderData: OrderData | null;
  onReceiptBack: () => void;

  // Order History (외부에서 데이터 관리)
  orderHistory: OrderHistoryItem[];
  onViewReceipt: (order: OrderHistoryItem) => void;

  // Fallback (메인 앱 렌더링)
  children: React.ReactNode;
}

/**
 * 페이지 네비게이션 이벤트 타입
 */
type PageNavigationEvent =
  | 'openLoginPage'
  | 'openProfilePage'
  | 'openOrderHistory'
  | 'openPrivacySettings'
  | 'openAnnouncements'
  | 'openTermsOfService'
  | 'openPrivacyPolicy'
  | 'openPaymentMethodPage'
  | 'navigateToEmailVerification'
  | 'navigateToBirthdayVerification';

/**
 * FullPageRouter
 * 전체 페이지 라우팅을 담당하며, 페이지 모달 상태를 내부에서 관리
 */
export function FullPageRouter({
  showPayment,
  onPaymentClose,
  onPaymentSuccess,
  allCartItems,
  allCartTotal,
  showTableOrderMenu,
  showCompanyList,
  onCompanyListBack,
  onCompanySelect,
  paymentFlow,
  orderData,
  onReceiptBack,
  orderHistory,
  onViewReceipt,
  children,
}: FullPageRouterProps) {
  // URL 기반 라우팅
  const location = useLocation();
  const _pathname = location.pathname;

  // 내부에서 페이지 모달 상태 관리
  const {
    showEmailVerification,
    closeEmailVerification,
    showBirthdayVerification,
    closeBirthdayVerification,
    showProfilePage,
    closeProfilePage,
    openProfilePage,
    showOrderHistory,
    closeOrderHistory,
    openOrderHistory,
    showPrivacySettings,
    closePrivacySettings,
    openPrivacySettings,
    showAnnouncements,
    closeAnnouncements,
    openAnnouncements,
    showTermsOfService,
    closeTermsOfService,
    openTermsOfService,
    showPrivacyPolicy,
    closePrivacyPolicy,
    openPrivacyPolicy,
    showPaymentMethodPage,
    closePaymentMethodPage,
    openPaymentMethodPage,
    showLoginPage,
    openLoginPage,
    closeLoginPage,
    openEmailVerification,
    openBirthdayVerification,
  } = usePageModals();

  // 내부에서 인증 상태 관리
  const {
    verifiedEmail,
    verifiedCompany,
    verifiedBirthday,
    verifyEmail,
    verifyBirthday,
    login,
  } = useAuth();

  // 인증 완료 핸들러
  const handleEmailVerificationComplete = useCallback((email: string, company: string) => {
    verifyEmail(email, company);
    closeEmailVerification();
  }, [verifyEmail, closeEmailVerification]);

  const handleBirthdayVerificationComplete = useCallback((birthday: string) => {
    verifyBirthday(birthday);
    closeBirthdayVerification();
  }, [verifyBirthday, closeBirthdayVerification]);

  // 로그인 핸들러
  const handleLoginWithClose = useCallback((email: string, password: string) => {
    login(email, password);
    closeLoginPage();
  }, [login, closeLoginPage]);

  // 이벤트 리스너 설정 - 외부 컴포넌트에서 페이지 열기 이벤트 수신
  useEffect(() => {
    const eventHandlers: Record<PageNavigationEvent, () => void> = {
      openLoginPage,
      openProfilePage,
      openOrderHistory,
      openPrivacySettings,
      openAnnouncements,
      openTermsOfService,
      openPrivacyPolicy,
      openPaymentMethodPage,
      navigateToEmailVerification: openEmailVerification,
      navigateToBirthdayVerification: openBirthdayVerification,
    };

    // 모든 이벤트 리스너 등록
    Object.entries(eventHandlers).forEach(([eventName, handler]) => {
      window.addEventListener(eventName, handler);
    });

    return () => {
      // 정리
      Object.entries(eventHandlers).forEach(([eventName, handler]) => {
        window.removeEventListener(eventName, handler);
      });
    };
  }, [
    openLoginPage,
    openProfilePage,
    openOrderHistory,
    openPrivacySettings,
    openAnnouncements,
    openTermsOfService,
    openPrivacyPolicy,
    openPaymentMethodPage,
    openEmailVerification,
    openBirthdayVerification,
  ]);

  // Login Page
  if (showLoginPage) {
    return (
      <LoginPage
        onBack={closeLoginPage}
        onLogin={handleLoginWithClose}
        onLoginComplete={closeLoginPage}
      />
    );
  }

  // Payment Page
  if (showPayment) {
    const generatedOrderNumber = !showTableOrderMenu
      ? `ORD${String(Math.floor(1 + Math.random() * 999)).padStart(3, '0')}`
      : undefined;

    return (
      <PaymentModal
        isOpen={showPayment}
        onClose={onPaymentClose}
        onPaymentSuccess={onPaymentSuccess}
        cartItems={allCartItems}
        totalAmount={allCartTotal + (allCartTotal >= 30000 ? 0 : 3000)}
        orderNumber={generatedOrderNumber}
        mode={showTableOrderMenu ? 'table' : 'pickup'}
      />
    );
  }

  // Company List Page
  if (showCompanyList) {
    return <CompanyListPage onBack={onCompanyListBack} onCompanySelect={onCompanySelect} />;
  }

  // Receipt Page
  if (paymentFlow === 'success' && orderData) {
    return (
      <ReceiptPage
        orderData={orderData}
        onBack={onReceiptBack}
      />
    );
  }

  // Email Verification Page
  if (showEmailVerification) {
    return (
      <EmailVerificationPage
        onBack={closeEmailVerification}
        onVerificationComplete={handleEmailVerificationComplete}
        currentEmail={verifiedEmail}
        currentCompany={verifiedCompany}
      />
    );
  }

  // Birthday Verification Page
  if (showBirthdayVerification) {
    return (
      <BirthdayVerificationPage
        onBack={closeBirthdayVerification}
        onVerificationComplete={handleBirthdayVerificationComplete}
        currentBirthday={verifiedBirthday}
      />
    );
  }

  // Profile Page
  if (showProfilePage) {
    return (
      <ProfilePage
        onBack={closeProfilePage}
      />
    );
  }

  // Order History Page
  if (showOrderHistory) {
    return (
      <OrderHistoryPage
        onBack={closeOrderHistory}
        orders={orderHistory}
        onViewReceipt={onViewReceipt}
      />
    );
  }

  if (showPrivacySettings) {
    return <PartnerVisibilitySettingsPage onBack={closePrivacySettings} />;
  }

  // Info Pages
  if (showAnnouncements) {
    return <AnnouncementsPage onBack={closeAnnouncements} />;
  }

  if (showPaymentMethodPage) {
    return <PaymentMethodPage onBack={closePaymentMethodPage} />;
  }

  if (showTermsOfService) {
    return <TermsOfServicePage onBack={closeTermsOfService} />;
  }

  if (showPrivacyPolicy) {
    return <PrivacyPolicyPage onBack={closePrivacyPolicy} />;
  }

  // 메인 앱 렌더링
  return <>{children}</>;
}
